import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import { useQueryClient } from 'react-query'
import Box from '@mui/material/Box'

import { locales } from 'config/languages'
import { ReactComponent as EnIcon } from 'assets/svg/en-EN.svg'
import { ReactComponent as PlIcon } from 'assets/svg/pl-PL.svg'
import { ReactComponent as DeIcon } from 'assets/svg/de-DE.svg'
import MenuPopover from 'shared/components/MenuPopover'
import { useSettingsMutation, useSettingsQuery } from 'shared/store/useSettings'

function getLanguageName(lang: string) {
   switch (lang) {
      case 'en-EN':
         return 'English'
      case 'pl-PL':
         return 'Polski'
      case 'de-DE':
         return 'Deutsch'
      default:
         return lang
   }
}

function getLanguageIcon(lang?: string) {
   switch (lang) {
      case 'en-EN':
         return <EnIcon />
      case 'pl-PL':
         return <PlIcon />
      case 'de-DE':
         return <DeIcon />
      default:
         return null
   }
}

export default function AuthorizedLanguagePopover() {
   const anchorRef = useRef(null)
   const queryClient = useQueryClient()
   const { i18n } = useTranslation()
   const [open, setOpen] = useState(false)
   const { data } = useSettingsQuery()
   const { mutate } = useSettingsMutation(true)

   function onChangeLang(lang: string) {
      i18n.changeLanguage(lang)
      if (data) {
         const payload: SettingsType = {
            ...data,
            userSettings: {
               ...data.userSettings,
               preferences: {
                  ...data.userSettings.preferences,
                  language: lang,
                  showProductNamesInCart: data.userSettings.preferences.showProductNamesInCart === 'true',
                  priceDifferences: data.userSettings.preferences.priceDifferences === 'true',
                  buyNowOrdersGroup: data.userSettings.preferences.buyNowOrdersGroup === 'true',
                  displayMinPopup: data.userSettings.preferences.displayMinPopup === 'true',
                  dontShowLocationDialog: data.userSettings.preferences.dontShowLocationDialog === 'true',
               },
            },
         }
         queryClient.invalidateQueries('userSettings')
         window.localStorage.setItem('prefLang', lang)
         mutate(payload, {
            onSettled: () => {
               if (window.location.href.includes('search')) {
                  window.location.reload()
               }
            },
         })
      }

      setOpen(false)
   }
   function handleOpenMenu() {
      setOpen(true)
   }
   function handleCloseMenu() {
      setOpen(false)
   }

   useEffect(() => {
      const settingsLang = data?.userSettings.preferences.language
      if (settingsLang && settingsLang !== i18n.language) {
         window.localStorage.setItem('prefLang', settingsLang)
         i18n.changeLanguage(settingsLang)
      }
   }, [i18n, data])

   return (
      <>
         <IconButton
            ref={anchorRef}
            onClick={handleOpenMenu}
            sx={{
               padding: 0,
            }}
         >
            {getLanguageIcon(i18n.language)}
         </IconButton>

         <MenuPopover
            open={open}
            onClose={handleCloseMenu}
            anchorEl={anchorRef.current}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
         >
            <Box sx={{ py: 1 }}>
               {locales?.map((lang) => (
                  <MenuItem
                     selected={lang.prefix === i18n.language}
                     key={lang.prefix}
                     onClick={() => onChangeLang(lang.prefix)}
                     sx={{ py: 1, px: 2.5 }}
                  >
                     <ListItemIcon sx={{ maxWidth: 25, maxHeight: 25 }}>{getLanguageIcon(lang.prefix)}</ListItemIcon>

                     <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                        {getLanguageName(lang.name)}
                     </ListItemText>
                  </MenuItem>
               ))}
            </Box>
         </MenuPopover>
      </>
   )
}
